(() => {
  if (window.CWISearch) return;
  const root = window.Shopify?.routes?.root || '/';
  let indexPromise;
  const normalize = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const withoutZeros = (value) => value.replace(/^([A-Z]*)0+(?=\d)/, '$1');
  const categories = [
    [/\bcoveralls?\b/, 'womens-coveralls', 'Women’s Coveralls'],
    [/\b(overalls?|bibs?)\b/, 'womens-overalls', 'Women’s Overalls'],
    [/\b(shorts?)\b/, 'womens-shorts', 'Women’s Shorts'],
    [/\b(pants?|trousers?)\b/, 'womens-pants', 'Women’s Pants'],
    [/\b(jackets?|parkas?|coats?)\b/, 'womens-jackets-and-parkas', 'Women’s Jackets and Parkas'],
    [/\b(hoodies?|sweaters?|sweatshirts?)\b/, 'womens-sweaters-and-hoodies', 'Women’s Sweaters and Hoodies'],
    [/\b(t[ -]?shirts?|tees?|tshirts?)\b/, 'womens-t-shirts', 'Women’s T-Shirts'],
    [/\bshirts?\b/, 'womens-work-shirts', 'Women’s Work Shirts'],
    [/\bvests?\b/, 'womens-vests', 'Women’s Vests']
  ];
  function category(query) {
    const term = query.toLowerCase().replace(/[’']/g, '');
    if (!/\b(womens?|ladies|ladys?|females?)\b/.test(term)) return null;
    const match = categories.find(([pattern]) => pattern.test(term));
    return { url: root + 'collections/' + (match?.[1] || 'womens-clothing'), title: match?.[2] || 'Women’s Workwear' };
  }
  function isPart(query) {
    return /\d/.test(query) && normalize(query).length >= 3;
  }
  function mentionsStyle(query, product) {
    const needle = normalize(query);
    const digits = query.replace(/\D/g, '');
    return (product.s || []).some((code) => {
      const value = normalize(code);
      const number = value.replace(/\D/g, '').replace(/^0+/, '');
      return value.length >= 3 && (needle.includes(value) || (number.length >= 3 && digits.includes(number)));
    });
  }
  function matchParts(query, records) {
    if (!isPart(query)) return [];
    const needle = normalize(query);
    const shortNeedle = withoutZeros(needle);
    return records.filter((product) => mentionsStyle(query, product) || product.c.some((code) => {
      const value = normalize(code);
      return value.includes(needle) || withoutZeros(value).includes(shortNeedle);
    })).sort((a, b) => {
      const exact = (p) => p.c.some((c) => normalize(c) === needle);
      return Number(exact(b)) - Number(exact(a)) || Number(mentionsStyle(query, b)) - Number(mentionsStyle(query, a));
    });
  }
  let indexLoadedAt = 0;
  async function liveIndex() {
    if (indexLoadedAt && Date.now() - indexLoadedAt > 60000) indexPromise = null;
    indexPromise ||= (async () => {
      const records = [];
      let page = 1;
      let next = true;
      while (next) {
        const url = new URL(root + 'collections/all', window.location.origin);
        url.searchParams.set('view', 'cwi-search-index');
        url.searchParams.set('page', page);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000) });
        if (!response.ok) throw new Error('Part lookup unavailable');
        const data = await response.json();
        records.push(...data.products);
        next = data.hasNextPage;
        page += 1;
      }
      indexLoadedAt = Date.now();
      return records.map((product) => {
        const titleCodes = [...product.t.matchAll(/\bstyle\s*[:#-]?\s*([A-Za-z0-9][A-Za-z0-9./_-]*)/gi)].map((match) => match[1]);
        const tokens = product.t.split(/[^A-Za-z0-9./_-]+/).filter((token) => /\d/.test(token) && token.length >= 3);
        const supplierCodes = product.c.filter(Boolean).map((code) => code.replace(/-(?:OR|RB|BK|NV|YL|LM)-/i, '-'));
        product.c = [...new Set([...product.c, ...supplierCodes, ...titleCodes, ...tokens])].filter(Boolean);
        product.s = [...new Set([...titleCodes, ...tokens])];
        product.q = titleCodes[0] || product.c[0] || product.t;
        return product;
      });
    })().catch((error) => { indexPromise = null; throw error; });
    return indexPromise;
  }
  async function standardMatches(query) {
    const url = new URL(root + 'search/suggest.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(8000) });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.resources?.results?.products || []).map((p) => ({
        h: p.handle, t: p.title, c: [], q: p.title, i: p.featured_image?.url || p.image || ''
      }));
    } catch (_) { return []; }
  }
  async function resolve(query) {
    const destination = category(query);
    if (destination) return { category: destination, query };
    if (!isPart(query)) return { query };
    const [index, native] = await Promise.allSettled([liveIndex(), standardMatches(query)]);
    const partial = index.status === 'fulfilled' ? matchParts(query, index.value) : [];
    const matches = [...partial];
    for (const product of native.status === 'fulfilled' ? native.value : []) {
      if (!matches.some((match) => match.h === product.h)) matches.push(product);
    }
    if (!matches.length) return { query };
    const exact = partial.filter((product) => product.c.some((code) => normalize(code) === normalize(query) || withoutZeros(normalize(code)) === withoutZeros(normalize(query))) || mentionsStyle(query, product));
    const terms = [...new Set([query, ...(exact.length ? exact : partial).map((product) => product.q)])];
    return { query: terms.map((term) => '"' + term.replace(/["\\]/g, '') + '"').join(' OR '), matches };
  }
  window.CWISearch = { category, isPart, matchParts, resolve };
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches?.('form.search') || form.dataset.cwiPassthrough === 'true') return;
    const input = form.querySelector('input[name="q"]');
    const original = input?.value.trim();
    if (!original || (!category(original) && !isPart(original))) return;
    if (form.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) return;
    event.preventDefault();
    const resolved = await resolve(original);
    if (input.value.trim() !== original) return;
    if (resolved.category) {
      window.location.assign(resolved.category.url);
    } else if (resolved.matches?.length) {
      const url = new URL(form.action, window.location.href);
      url.search = new URLSearchParams(new FormData(form)).toString();
      url.searchParams.set('q', resolved.query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('cwi_original', original);
      window.location.assign(url.toString());
    } else {
      form.dataset.cwiPassthrough = 'true';
      try { form.requestSubmit(); } finally { delete form.dataset.cwiPassthrough; }
    }
  }, true);
  const original = new URLSearchParams(window.location.search).get('cwi_original');
  if (original && window.location.pathname.endsWith('/search')) {
    document.querySelectorAll('input[name="q"]').forEach((input) => { input.value = original; });
  }
})();
